import { useCallback, useMemo } from 'react';
import { actions, makePropGetter, useGetLatest } from 'react-table';

import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@material-ui/icons/KeyboardArrowUp';
import { IconButton } from '@material-ui/core';

actions.resetSubTableExpanded = 'resetSTE';
actions.toggleSubTableExpanded = 'toggleSTRE';
actions.toggleAllSubTableExpanded = 'toggleARE';

const defaultGetToggleAllSubTableProps = (props, { instance }) => [
  props,
  {
    onClick: () => {
      instance.toggleAllSubTable();
    },
    style: {
      cursor: 'pointer',
    },
    title: 'Toggle all expanded.',
  },
];

const defaultGetToggleSubTableProps = (props, { instance, row }) => [
  props,
  {
    onClick: () => {
      instance.toggleSubTable(row.id);
    },
    style: {
      cursor: 'pointer',
    },
    title: 'Toggle expanded.',
  },
];

function reducer(state, action, previousState, instance) {
  switch (action.type) {
    case actions.init: {
      return {
        subTableOpen: {},
        ...state,
      };
    }
    case actions.resetSubTableExpanded: {
      return {
        ...state,
        subTableOpen: instance.initialState.subTableOpen || {},
      };
    }
    case actions.toggleAllSubTableExpanded: {
      const { isAllSubTableExpanded, rowsById } = instance;

      return {
        ...state,
        subTableOpen: isAllSubTableExpanded
          ? {}
          : Object.fromEntries(Object.keys(rowsById).map(id => [id, true])),
      };
    }

    case actions.toggleSubTableExpanded:
      const { id, value: setExpanded } = action;
      const currentValue = state.subTableOpen[id];
      const toggleMode = typeof setExpanded === 'undefined';
      return {
        ...state,
        subTableOpen: {
          ...state.subTableOpen,
          [id]: toggleMode ? !currentValue : setExpanded,
        },
      };
  }
}

function useInstance(instance) {
  const {
    rowsById,
    getHooks,
    state: { subTableOpen },
    dispatch,
    renderSubTable = defaultRenderSubTable,
  } = instance;

  let isAllSubTableExpanded = useMemo(
    () => Object.keys(rowsById).length === Object.keys(subTableOpen).length,
    [rowsById, subTableOpen]
  );

  const getInstance = useGetLatest(instance);

  const toggleAllSubTable = useCallback(
    value => dispatch({ type: actions.toggleAllSubTableExpanded, value }),
    [dispatch]
  );

  const toggleSubTable = useCallback(
    (id, value) => dispatch({ type: actions.toggleSubTableExpanded, value, id }),
    [dispatch]
  );

  const getToggleAllSubTableProps = makePropGetter(getHooks().getToggleAllSubTableProps, {
    instance: getInstance(),
  });

  Object.assign(instance, {
    isAllSubTableExpanded,
    toggleAllSubTable,
    toggleSubTable,
    renderSubTable,
    getToggleAllSubTableProps,
  });
}

function prepareRow(row, { instance: { getHooks }, instance }) {
  row.toggleSubTable = set => instance.toggleSubTable(row.id, set);
  row.isSubTableOpen = instance.state.subTableOpen[row.id];
  row.getToggleSubTableProps = makePropGetter(getHooks().getToggleSubTableProps, {
    instance,
    row,
  });
  row.renderSubTable = () => instance.renderSubTable({ instance, row });
}

function defaultRenderSubTable() {
  return <span>Content Missing</span>;
}

export function useSubTable(hooks) {
  hooks.getToggleAllSubTableProps = [defaultGetToggleAllSubTableProps];
  hooks.getToggleSubTableProps = [defaultGetToggleSubTableProps];
  // hooks.renderSubTable = [defaultRenderSubTable];
  hooks.stateReducers.push(reducer);
  hooks.useInstance.push(useInstance);
  hooks.prepareRow.push(prepareRow);
  hooks.allColumns.push(columns => [
    {
      id: 'showtable',
      disableFilters: true,
      Header: ({ getToggleAllSubTableProps, isAllSubTableExpanded }) => (
        // null,
        <IconButton aria-label="expand all" size="small" {...getToggleAllSubTableProps()}>
          {isAllSubTableExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
        </IconButton>
      ),
      Cell: ({ row }) => (
        <IconButton aria-label="expand all" size="small" {...row.getToggleSubTableProps()}>
          {row.isSubTableOpen ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
        </IconButton>
      ),
    },
    ...columns,
  ]);
}

useSubTable.pluginName = 'useSubTable';
